// PR6 3d/3e — the mobile player (ui_kits/mobile/Mobile.jsx's MPlayer) and the mobile notes
// layout (MNotes) collapsed into one screen: a full-width player with Transcript and Notes
// tabs below it. There is no separate "meeting vs. recording" entity in this codebase (M4/F1
// haven't shipped — see REQUIREMENTS §4), so artboard 3e has nothing of its own to show yet;
// the Notes tab is the honest placeholder FR-M7 asks for ("nothing generated is shown without
// its source") until a real notes pipeline exists. "Comments" and view counts are not ported
// here (FR-EN7).
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from "react";
import { Icon, IconButton, Tabs } from "@grackle/ui";
import type { WatchOut } from "../../api/types";
import type { GrackleTranscript } from "../../live/types";
import { formatDuration, formatRelativeTime } from "../../library/format";
import type { PlayerHandle } from "../../watch/Player";
import TranscriptPanel from "../../watch/TranscriptPanel";
import { usePlayableSource } from "../../watch/playableSource";
import { cuesFromTranscript } from "../../watch/transcript";
import MobileShareSheet from "./MobileShareSheet";
import "./MobilePlayerScreen.css";

const Player = lazy(() => import("../../watch/Player"));

export default function MobilePlayerScreen({
  grackleId,
  watch,
  transcript,
  userId,
  onBack,
  onReloadWatch,
}: {
  grackleId: string;
  watch: WatchOut;
  /** Loaded by the page (reset PR3 WatchPage); the panel follows playback and seeks on tap. */
  transcript: GrackleTranscript | null;
  userId?: string | null;
  onBack: () => void;
  /** Re-fetches `watch`; lets the player re-sign an expired progressive URL (FR-ST18). */
  onReloadWatch?: () => unknown;
}) {
  const playerRef = useRef<PlayerHandle>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const cues = useMemo(() => cuesFromTranscript(transcript), [transcript]);
  const onSeek = useCallback((t: number) => playerRef.current?.seek(t), []);
  const [tab, setTab] = useState<"transcript" | "notes">("transcript");
  const [shareOpen, setShareOpen] = useState(false);
  const { grackle } = watch;
  // SD60: plays while processing, and the source stays put while WatchPage polls.
  const { source, refresh } = usePlayableSource(watch, onReloadWatch);

  return (
    <div className="mplayer">
      <div className="mplayer__topbar">
        <IconButton icon="arrow-left" label="Back" size="sm" onClick={onBack} />
        <span className="mplayer__topbar-title">{grackle.title}</span>
        <IconButton icon="link-simple" label="Share" size="sm" onClick={() => setShareOpen(true)} />
      </div>

      {source ? (
        <Suspense fallback={<div className="mplayer__status" />}>
          <Player
            ref={playerRef}
            recordingId={grackle.id}
            userId={userId}
            durationMs={grackle.duration_ms}
            onTime={setCurrentTime}
            src={source.src}
            kind={source.kind}
            onSourceExpired={refresh}
            poster={watch.poster_url}
            title={grackle.title}
            sourceWidth={grackle.source_width}
            sourceHeight={grackle.source_height}
            renditions={watch.renditions}
          />
        </Suspense>
      ) : (
        <div className="mplayer__status">
          {grackle.status === "failed"
            ? "Processing failed. The original upload is safe."
            : grackle.status === "uploading"
              ? "Still uploading — this updates on its own."
              : "Still processing — this updates on its own."}
        </div>
      )}

      <div className="mplayer__meta">
        <div className="mplayer__meta-title">{grackle.title}</div>
        <div className="mplayer__meta-sub">
          {grackle.owner_name}
          {grackle.duration_ms != null ? ` · ${formatDuration(grackle.duration_ms)}` : ""} ·{" "}
          {formatRelativeTime(grackle.created_at)}
        </div>
      </div>

      <Tabs
        variant="solid"
        className="mplayer__tabs"
        items={[
          { value: "transcript", label: "Transcript" },
          { value: "notes", label: "Notes" },
        ]}
        value={tab}
        onChange={(v) => setTab(v as "transcript" | "notes")}
      />

      <div className="mplayer__tabbody">
        {tab === "transcript" ? (
          <TranscriptPanel
            status={grackle.transcript_status}
            transcript={transcript}
            cues={cues}
            currentTime={currentTime}
            onSeek={onSeek}
          />
        ) : (
          <div className="mplayer__notes-empty">
            <Icon name="sparkle" size={22} />
            <div>
              Notes aren't available for this recording yet. When Grackle generates notes,
              every line will point back to the moment it came from.
            </div>
          </div>
        )}
      </div>

      {shareOpen ? (
        <MobileShareSheet
          grackleId={grackleId}
          title={grackle.title}
          meta={grackle.duration_ms != null ? formatDuration(grackle.duration_ms) : undefined}
          onClose={() => setShareOpen(false)}
        />
      ) : null}
    </div>
  );
}
